/**
 * Utility functions for initializing the Voice Lab integration with the Voice Actor management system
 */

import store from './store';
import {
  fetchActorUsers,
  fetchActorPortfolios,
  fetchRecordedCharacters,
  fetchCharacterVariations,
  initializeActorSchema
} from '../slices/voiceActorSlice';

/**
 * Initialize the Voice Actor management system
 * This function should be called when the application starts
 * @returns {Promise<void>}
 */
export const initializeVoiceActorSystem = async () => {
  try {
    // Get the current state
    const state = store.getState();
    const actorUsers = state.user?.voiceActor?.actorUsers?.items || [];

    // Log API base URL for debugging
    console.log('API Base URL:', store.getState().constants?.config?.BASE_URL || 'Not found in store');

    // No longer automatically create a default actor
    console.log('Fetching actor users...');

    try {
      // Always fetch actor users first
      await store.dispatch(fetchActorUsers()).unwrap();

      // Get updated actor list after fetching
      const updatedState = store.getState();
      const updatedActorUsers = updatedState.user?.voiceActor?.actorUsers?.items || [];
      console.log('Actor users fetched:', updatedActorUsers.length);

      if (updatedActorUsers.length > 0) {
        console.log('Actor users found, fetching related data');
        console.log('Existing actor users:', updatedActorUsers);

        // Fetch portfolios for each actor user
        for (const actor of updatedActorUsers) {
          console.log(`Fetching portfolios for actor ${actor.uuid}...`);
          await store.dispatch(fetchActorPortfolios({ actorId: actor.uuid })).unwrap();
          console.log(`Portfolios for actor ${actor.uuid} fetched successfully`);
        }

        // Get the updated state
        const portfolioState = store.getState();
        const portfolios = portfolioState.user?.voiceActor?.actorPortfolios?.items || [];
        console.log('Portfolios fetched:', portfolios);

        // Fetch characters for each portfolio
        for (const portfolio of portfolios) {
          console.log(`Fetching characters for portfolio ${portfolio.uuid}...`);
          await store.dispatch(fetchRecordedCharacters(portfolio.uuid)).unwrap();
          console.log(`Characters for portfolio ${portfolio.uuid} fetched successfully`);
        }

        // Get the updated state again
        const characterState = store.getState();
        const characters = characterState.user?.voiceActor?.recordedCharacters?.items || [];
        console.log('Characters fetched:', characters);

        // Fetch variations for each character
        for (const character of characters) {
          console.log(`Fetching variations for character ${character.uuid}...`);
          await store.dispatch(fetchCharacterVariations(character.uuid)).unwrap();
          console.log(`Variations for character ${character.uuid} fetched successfully`);
        }
      }
    } catch (fetchError) {
      console.error('Error during data fetching:', fetchError);
      console.error('Error details:', fetchError.message);
      if (fetchError.stack) console.error('Error stack:', fetchError.stack);
      throw fetchError; // Re-throw to be caught by the outer try/catch
    }

    console.log('Voice Actor management system initialized successfully');
    return true;
  } catch (error) {
    console.error('Error initializing Voice Actor management system:', error);
    console.error('Error type:', error.name);
    console.error('Error message:', error.message);
    if (error.response) {
      console.error('API Response status:', error.response.status);
      console.error('API Response data:', error.response.data);
    }
    return false;
  }
};

/**
 * Check if the Voice Actor management system is initialized
 * @returns {boolean}
 */
export const isVoiceActorSystemInitialized = () => {
  const state = store.getState();
  // Consider the system initialized if we've fetched the actor users list,
  // even if it's empty (status will be 'succeeded' after fetching)
  const status = state.user?.voiceActor?.actorUsers?.status;
  return status === 'succeeded' || status === 'idle';
};

/**
 * Get the initialization status of the Voice Actor management system
 * @returns {Object} Object containing the initialization status
 */
export const getVoiceActorSystemStatus = () => {
  const state = store.getState();
  const actorUsers = state.user?.voiceActor?.actorUsers?.items || [];
  const portfolios = state.user?.voiceActor?.actorPortfolios?.items || [];
  const characters = state.user?.voiceActor?.recordedCharacters?.items || [];
  const variations = state.user?.voiceActor?.characterVariations?.items || [];
  const status = state.user?.voiceActor?.actorUsers?.status;

  return {
    // System is initialized if we've fetched the actor list, even if it's empty
    initialized: status === 'succeeded' || status === 'idle',
    actorUsersCount: actorUsers.length,
    portfoliosCount: portfolios.length,
    charactersCount: characters.length,
    variationsCount: variations.length,
    recordedVariationsCount: variations.filter(v => v.s3Url).length
  };
};
